import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getActionColor, getBandColor, getStatusPieColor, titleCase } from "../report.utils";

const tooltipStyle = {
  borderRadius: 8,
  border: "1px solid #d8dee8",
  boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
  background: "#fff",
};

function EmptyChart({ label }) {
  return (
    <Box sx={{ height: 220, display: "grid", placeItems: "center", color: "text.secondary" }}>
      <Typography variant="body2">{label}</Typography>
    </Box>
  );
}

function ChartCard({ title, subtitle, children }) {
  const theme = useTheme();

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.15,
        borderRadius: "8px",
        borderColor: theme.hr?.line?.soft || alpha(theme.palette.primary.main, 0.14),
        background: alpha(theme.palette.background.paper, 0.94),
        boxShadow: "none",
      }}
    >
      <Stack spacing={0.2} sx={{ mb: 0.75 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      </Stack>
      {children}
    </Paper>
  );
}

export default function ReportCharts({
  summary,
  statusDistribution,
  activeBand,
  activeAction,
  activeStatus,
  onBandClick,
  onActionClick,
  onStatusClick,
}) {
  const bandData = Array.isArray(summary?.by_band) ? summary.by_band : [];
  const actionData = Array.isArray(summary?.by_recommended_action)
    ? summary.by_recommended_action
    : [];
  const statusData = Array.isArray(statusDistribution) ? statusDistribution : [];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", xl: "repeat(3, minmax(0, 1fr))" },
        gap: 0.8,
        width: "100%",
      }}
    >
      <ChartCard
        title="Band Distribution"
        subtitle="Click a bar to apply a percentage-band quick filter."
      >
        {bandData.length === 0 ? (
          <EmptyChart label="No band data" />
        ) : (
          <Box sx={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bandData} margin={{ top: 4, right: 10, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e7ebf3" />
                <XAxis dataKey="band" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip wrapperStyle={tooltipStyle} />
                <Legend />
                <Bar dataKey="count" name="Appraisals" radius={[6, 6, 0, 0]}>
                  {bandData.map((entry) => (
                    <Cell
                      key={entry.band}
                      fill={activeBand === entry.band ? "#0b4f99" : getBandColor(entry.band)}
                      cursor="pointer"
                      onClick={() => onBandClick(entry.band)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </ChartCard>

      <ChartCard
        title="Recommended Actions"
        subtitle="Click a bar to filter by action recommendation type."
      >
        {actionData.length === 0 ? (
          <EmptyChart label="No recommendation data" />
        ) : (
          <Box sx={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={actionData} margin={{ top: 4, right: 10, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e7ebf3" />
                <XAxis dataKey="action" tickFormatter={titleCase} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip wrapperStyle={tooltipStyle} />
                <Legend />
                <Bar dataKey="count" name="Count" radius={[6, 6, 0, 0]}>
                  {actionData.map((entry) => (
                    <Cell
                      key={entry.action}
                      fill={activeAction === entry.action ? "#0b4f99" : getActionColor(entry.action)}
                      cursor="pointer"
                      onClick={() => onActionClick(entry.action)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </ChartCard>

      <ChartCard
        title="Status Distribution"
        subtitle="Click a slice to filter by workflow status."
      >
        {statusData.length === 0 ? (
          <EmptyChart label="No status data" />
        ) : (
          <Box sx={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  dataKey="count"
                  nameKey="status"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  innerRadius={46}
                  paddingAngle={2}
                >
                  {statusData.map((entry) => (
                    <Cell
                      key={entry.status}
                      fill={activeStatus === entry.status ? "#0b4f99" : getStatusPieColor(entry.status)}
                      cursor="pointer"
                      onClick={() => onStatusClick(entry.status)}
                    />
                  ))}
                </Pie>
                <Legend formatter={(value) => titleCase(value)} />
                <Tooltip wrapperStyle={tooltipStyle} formatter={(v, n) => [v, titleCase(n)]} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        )}
      </ChartCard>
    </Box>
  );
}
